<template>
  <block-item-card
    ref="dynamicEvolution"
    :title="$t('ALM.Section.LoansPortfolio.DynamicEvolution.Title')"
    :tooltip="$t('ALM.Section.LoansPortfolio.DynamicEvolution.InfoTip')"
    class="fill-height d-flex flex-column"
  >
    <v-col class="pa-6 ">
      <v-row>
        <!-- Top Left Chart: Size Total Value of Deposits -->
        <v-col
          cols="12"
          md="6"
          class="gradient-grey-06-bg rounded-lg"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <div
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-blue-04-base);"
                >
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Size.Title') }}
                </div>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Size.SubTitle') }}
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span
                  class="metric-value"
                  :class="totalValueChange >= 0 ? 'positive' : 'negative'"
                >{{ totalValueChange >= 0 ? '+' : '-' }}{{ formattedTotalValueChange }}</span>
              </div>
            </div>
            <v-lazy>
              <e-chart
                :ref="`echart-size-${chartRefId}`"
                :autoresize="true"
                :option="sizeChartOptions"
                :style="`height: ${chartHeight}px;`"
                class="chart"
              />
            </v-lazy>
          </div>
        </v-col>

        <!-- Top Right Chart: Deposits Number of Accounts -->
        <v-col
          cols="12"
          md="6"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-grey-01-base);"
                >
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Loans.Title') }}
                </h3>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Loans.SubTitle') }}
                </div>
              </div>

              <div class="chart-legend">
                <asset-button-group
                  :items="loanTypes"
                  :selected="selectedLoanType"
                  :mandatory="true"
                  :full-width="true"
                  :height="24"
                  @update:selected="onLoanTypeChange"
                />
              </div>
            </div>
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <div style="height:350px;">
                <e-chart
                  :key="`loans-chart-${selectedLoanType}-${chartRefId}`"
                  :autoresize="true"
                  :option="loansChartOptions"
                  :style="`height: ${chartHeight}px;margin-top: 32px;`"
                  class="chart"
                />
              </div>
            </v-lazy>
          </div>
        </v-col>
      </v-row>
      <v-row class="mt-10">
        <!-- Bottom Left Chart: Yield Average Yield Percentage -->
        <v-col
          cols="12"
          md="6"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex align-center">
                <h3
                  class="chart-title"
                  style="color: var(--v-synthesis-ui-purple-02-base);"
                >
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.Title') }}
                </h3>
                <div class="chart-subtitle">
                  {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.SubTitle') }}
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span :class="['metric-value', durationChange >= 0 ? 'positive' : 'negative']">
                  {{ durationChange >= 0 ? '+' : '' }}{{ durationChange }} {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.MetricUnit') }}
                </span>
              </div>
            </div>
            <v-lazy>
              <e-chart
                :ref="`echart-yield-${chartRefId}`"
                :autoresize="true"
                :option="durationChartOptions"
                :style="`height: ${chartHeight}px;`"
                class="chart"
              />
            </v-lazy>
          </div>
        </v-col>

        <!-- Bottom Right Chart: Interest Expense Annualized -->
        <v-col
          cols="12"
          md="6"

          class="gradient-grey-06-bg  rounded-lg"
        >
          <div class="chart-container">
            <div class="chart-header">
              <div class="d-flex  flex-column">
                <div class="d-flex">
                  <h3
                    class="chart-title"
                    style="color: var(--v-synthesis-ui-green-01-base);"
                  >
                    {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Interest.Title') }}
                  </h3>
                  <div class="chart-subtitle">
                    {{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.Interest.SubTitle') }}
                  </div>
                </div>
                <div class="mt-2">
                  <banking-switch
                    v-model="interestOutcomeSwitch"
                    :enabled-label="$t('ALM.Section.LoansPortfolio.DynamicEvolution.Interest.SwitchEnabledLabel')"
                    :disabled-label="$t('ALM.Section.LoansPortfolio.DynamicEvolution.Interest.SwitchDisabledLabel')"
                  />
                </div>
              </div>

              <div class="chart-metric">
                <span class="metric-label">{{ $t('ALM.Section.LoansPortfolio.DynamicEvolution.UpdateInterval') }}</span>
                <span :class="['metric-value', interestIncomeRaw >= 0 ? 'positive' : 'negative']">
                  {{ interestIncomeRaw >= 0 ? '+' : '-' }}{{ interestIncomeValueFmt }}
                </span>
              </div>
            </div>
            <v-lazy>
              <e-chart
                :ref="`echart-interest-${chartRefId}`"
                :autoresize="true"
                :option="interestChartOptions"
                :style="`height: ${chartHeight}px;`"
                class="chart"
              />
            </v-lazy>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import {
  toNumberFormatNotationShort,
  toDecimalSignificant,
  computeYAxisBounds
} from '@/lib/utils/helper'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'

import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'

use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'ALMDynamicEvolution',
  components: { BlockItemCard, AssetButtonGroup, BankingSwitch },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, chartsColorMixin, riskAnalysisId],
  props     : {},
  dataStore : {
    appToolbarExtended  : 'App.Config.AppToolbarExtended',
    Loans               : 'ALM.Loans.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      breakpoint           : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      chartRefId           : Date.now(),
      selectedLoanType     : 'Total',
      interestOutcomeSwitch: true
    }
  },
  computed: {

    toNumberFormatNotationShort () {
      return toNumberFormatNotationShort
    },
    chartHeight () {
      return 500
    },

    dynamicEvolution () {
      return this.Loans.DynamicEvolution || null
    },

    // Format dates for display (Apr24, May24, etc.)
    formattedDates () {
      if (!this.dynamicEvolution?.dates) return []
      return this.dynamicEvolution.dates.map(date => {
        const d = new Date(date)
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString().slice(-2)
        return `${month}${year}`
      })
    },

    // Size Total Value Chart Options
    sizeChartOptions () {
      if (!this.dynamicEvolution) return {}
      const y = computeYAxisBounds(this.dynamicEvolution.totalValue)
      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value = toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.LoansPortfolio.DynamicEvolution.Size.Title')}: $${value}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : y.min,
          max      : y.max,
          interval : y.interval,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(value) || 0)
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Total Value',
          type     : 'line',
          data     : this.dynamicEvolution.totalValue,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-blue-04')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 10,
          smooth    : true
        }]
      }
    },

    // Loans Chart Options (Stacked Bar)
    loansChartOptions () {
      if (!this.dynamicEvolution) return {}

      let series = []

      if (this.selectedLoanType === 'Total') {
        // Show all loan types stacked
        series = this.dynamicEvolution.typeNames.map((typeName, index) => ({
          name : typeName,
          type : 'bar',
          stack: 'total',
          data : this.dynamicEvolution.amountOfContracts.map(month =>
            month.perTypeAmountOfContracts[index]?.amountOfContracts || 0
          ),
          itemStyle: {
            color: this.getLoanTypeIconColor(index)
          },
          barWidth: '8px'
        }))
      } else {
        // Show only the selected loan type
        const selectedIndex = this.dynamicEvolution.typeNames.indexOf(this.selectedLoanType)
        if (selectedIndex !== -1) {
          series = [{
            name: this.selectedLoanType,
            type: 'bar',
            data: this.dynamicEvolution.amountOfContracts.map(month =>
              month.perTypeAmountOfContracts[selectedIndex]?.amountOfContracts || 0
            ),
            itemStyle: {
              color: this.getLoanTypeIconColor(selectedIndex)
            },
            barWidth: '8px'
          }]
        }
      }

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            // Custom formatter for loans chart
            let result = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`

            // For Total Case, show Total as first number in the list
            if (this.selectedLoanType === 'Total') {
              const totalValue = params.reduce((sum, param) => sum + (param.value || 0), 0)
              result += `<div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${this.themeColorRGBA('synthesis-ui-grey-03')}; margin-right: 8px;"></span>
                <span>Total: ${toDecimalSignificant(totalValue, 5)}</span>
              </div>`
            }

            params.forEach(param => {
              result += `<div style="margin: 4px 0;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; margin-right: 8px;"></span>
                <span>${param.seriesName}: ${toDecimalSignificant(param.value, 5)}</span>
              </div>`
            })

            return result
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true

        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -15,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom'
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series
      }
    },

    // Yield Chart Options (Bar Chart)
    durationChartOptions () {
      if (!this.dynamicEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.Title')}: ${params[0].value} ${this.$t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.MetricUnit')}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 11
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          },
          splitArea: {
            areaStyle: {
              color     : ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)'],
              shadowBlur: 0.5
            }
          }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value, index) => {
              return value + ' ' + this.$t('ALM.Section.LoansPortfolio.DynamicEvolution.Duration.MetricUnit')
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Yield',
          type     : 'bar',
          data     : this.dynamicEvolution.duration,
          itemStyle: {
            color       : this.themeColorRGBA('synthesis-ui-purple-02'),
            borderRadius: [4, 4, 0, 0]
          },
          barWidth: '8px'
        }]
      }
    },

    // Interest Chart Options (Line Chart)
    interestChartOptions () {
      if (!this.dynamicEvolution) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            const value = toNumberFormatNotationShort(params[0].value)
            return `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>
                    <div style="margin: 4px 0;">
                      <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                      <span>${this.$t('ALM.Section.LoansPortfolio.DynamicEvolution.Interest.Title')}: ${this.interestOutcomeSwitch ? this.riskBaseCurrencyDisplayName + value : value + '%'}</span>
                    </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 35,
          top         : 50,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.interestIncomeChartData).min,
          max      : computeYAxisBounds(this.interestIncomeChartData).max,
          interval : computeYAxisBounds(this.interestIncomeChartData).interval,
          axisLabel: {
            margin       : -25,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return this.interestOutcomeSwitch ? this.riskBaseCurrencyDisplayName + (toNumberFormatNotationShort(value) || 0) : toDecimalSignificant(value, 2) + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }

        },
        series: [{
          name     : 'Interest Income',
          type     : 'line',
          data     : this.interestIncomeChartData,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-green-01')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 10,
          smooth    : true
        }]
      }
    },

    // Data for metrics
    totalValueChange () {
      return this.dynamicEvolution?.totalValueChange || 0
    },

    formattedTotalValueChange () {
      const v = toNumberFormatNotationShort(Math.abs(this.totalValueChange))

      return `${this.riskBaseCurrency.displaySymbol}${v}`
    },

    amountOfContractsChange () {
      return this.dynamicEvolution?.amountOfContractsChange || 0
    },

    durationChange () {
      return this.dynamicEvolution?.durationChange || 0
    },
    interestIncomeChangeYield () {
      return this.dynamicEvolution?.interestIncomeChangeYield || 0
    },
    interestIncomeYield () {
      return this.dynamicEvolution?.interestIncomeYield || []
    },

    interestIncomeChangeValue () {
      return this.dynamicEvolution?.interestIncomeChangeValue || 0
    },

    interestIncomeValue () {
      return this.dynamicEvolution?.interestIncomeValue || []
    },

    interestIncomeRaw () {
      return this.interestOutcomeSwitch ? this.interestIncomeChangeValue : this.interestIncomeChangeYield
    },
    interestIncomeValueFmt () {
      return this.interestOutcomeSwitch ? this.riskBaseCurrencyDisplayName + toNumberFormatNotationShort(Math.abs(this.interestIncomeChangeValue)) : Math.abs(this.interestIncomeChangeYield)
    },
    interestIncomeChartData () {
      return this.interestOutcomeSwitch ? this.interestIncomeValue : this.interestIncomeYield
    },
    loanTypes () {
      const loanTypes = this.Loans.DynamicEvolution.typeNames.map((type, index) => ({
        Value: type,
        Title: type,
        icon : {
          color: this.getLoanTypeIconColor(index)
        }
      }))

      return [{ Value: 'Total', Title: 'Total' }, ...loanTypes]
    },

    // Dynamic Y-axis minimum calculations
    sizeChartYAxisMin () {
      if (!this.dynamicEvolution?.totalValue) return undefined

      const values = this.dynamicEvolution.totalValue.filter(val => val !== null && val !== undefined && val !== '-')
      if (values.length === 0) return undefined

      const min = Math.min(...values)
      const max = Math.max(...values)
      const range = max - min

      if (range < max * 0.1) {
        const padding = Math.max(max * 0.1, 1000)
        return Math.max(0, min - padding)
      }

      return Math.max(0, min - range * 0.1)
    }
  },

  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }

  },

  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  destroyed () {},

  methods: {
    getLoanTypeIconColor (index) {
      return ['#EA804B', '#57C05B', '#C264DE', '#FFB21E'][index % 4]
    },

    onLoanTypeChange (newLoanType) {
      this.selectedLoanType = newLoanType
    }
  }
}
</script>

<style scoped>

.gradient-grey-06-bg {
  background-color: var(--v-synthesis-ui-grey-06-base) !important;
}

.chart-title {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.chart-subtitle {
  margin-left: 8px;
  color: var(--v-synthesis-ui-grey-01-base);
  font-size: 10px;
  position: relative;
  top: 1px;
}

.metric-label {
  font-size: 10px;
  color: var(--v-synthesis-brand-dark-blue-base);
  margin-bottom: 2px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  gap: 2px;
  align-items: flex-start;
  padding: 0 8px;
}

.chart-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.metric-value {
  font-size: 12px;
  font-weight: 500;
}

.metric-value.positive {
  color: #4CAF50;
  font-size: 28px;
}

.metric-value.negative {
  color: #FF5722;
  font-size: 28px;
}

.chart-legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #666;
}

.legend-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

</style>
